import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Calendar } from "../components/ui/calendar";
import { toast } from "sonner";
import {
  ArrowLeft,
  CreditCard,
  MailCheck,
  CalendarIcon,
  Tag,
  Check,
  X,
} from "lucide-react";
import { useI18n, formatPrice } from "../i18n";
import {
  fetchCar,
  fetchCarAvailability,
  createReservationRequest,
  createCheckoutSession,
  checkReferral,
} from "../api";

const toIsoDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const Booking = () => {
  const { carId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { t, lang } = useI18n();
  const [car, setCar] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [pickup, setPickup] = useState();
  const [ret, setRet] = useState();
  const [showPickupCal, setShowPickupCal] = useState(false);
  const [showReturnCal, setShowReturnCal] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    pickup_location: "",
    flight_number: "",
    airport_pickup: false,
    notes: "",
  });
  const [referralInput, setReferralInput] = useState(params.get("ref") || "");
  const [referralCheck, setReferralCheck] = useState(null); // { valid, discount }
  const [referralChecking, setReferralChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCar(carId).then(setCar).catch(() => setCar(null));
    fetchCarAvailability(carId)
      .then((d) => setBlocked(d.blocked || []))
      .catch(() => setBlocked([]));
  }, [carId]);

  // Auto-apply referral code if it came from a URL param
  useEffect(() => {
    const ref = params.get("ref");
    if (ref) {
      onCheckReferral(ref);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const days = useMemo(() => {
    if (!pickup || !ret) return 0;
    const ms = new Date(ret) - new Date(pickup);
    return Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
  }, [pickup, ret]);

  const isDateBlocked = (date) => {
    const iso = toIsoDate(date);
    return blocked.some((b) => iso >= b.start && iso < b.end);
  };

  const baseTotal = useMemo(() => {
    if (!car || days <= 0) return 0;
    return days * car.price_per_day;
  }, [car, days]);

  const discount = referralCheck?.valid ? referralCheck.discount_usd : 0;
  const total = Math.max(0, baseTotal - discount);

  const onCheckReferral = async (codeArg) => {
    const code = (codeArg ?? referralInput).trim();
    if (!code) return;
    setReferralChecking(true);
    try {
      const res = await checkReferral(code);
      setReferralCheck(res);
      setReferralInput(code);
      if (res.valid) {
        toast.success(
          lang === "pt"
            ? `Código válido — $${res.discount_usd} de desconto!`
            : `Code applied — $${res.discount_usd} off!`
        );
      } else {
        toast.error(
          lang === "pt"
            ? "Código não encontrado."
            : "Code not found."
        );
      }
    } catch {
      setReferralCheck({ valid: false });
    } finally {
      setReferralChecking(false);
    }
  };

  const clearReferral = () => {
    setReferralCheck(null);
    setReferralInput("");
  };

  const validate = () => {
    if (!form.full_name || !form.email || !form.phone || !form.pickup_location) {
      toast.error(t.booking.missingFields);
      return false;
    }
    if (form.airport_pickup && !form.flight_number) {
      toast.error(t.booking.missingFields);
      return false;
    }
    if (!pickup || !ret) {
      toast.error(t.booking.missingFields);
      return false;
    }
    if (days <= 0) {
      toast.error(t.booking.datesInvalid);
      return false;
    }
    return true;
  };

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleAirportPickup = () => {
    setForm((prev) => {
      const next = !prev.airport_pickup;
      return {
        ...prev,
        airport_pickup: next,
        pickup_location: next
          ? "Logan International Airport (BOS) — Terminal arrivals"
          : prev.pickup_location ===
            "Logan International Airport (BOS) — Terminal arrivals"
          ? ""
          : prev.pickup_location,
      };
    });
  };

  const buildPayload = () => ({
    car_id: car.id,
    ...form,
    pickup_date: toIsoDate(pickup),
    return_date: toIsoDate(ret),
    referral_code: referralCheck?.valid ? referralInput.trim().toUpperCase() : undefined,
  });

  const onRequestOnly = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await createReservationRequest(buildPayload());
      toast.success(t.booking.success);
      navigate(`/booking/success?request_id=${res.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  const onPayNow = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = { ...buildPayload(), origin_url: window.location.origin };
      const res = await createCheckoutSession(payload);
      window.location.href = res.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
      setSubmitting(false);
    }
  };

  if (!car) {
    return <div className="max-w-7xl mx-auto px-6 py-32 text-zinc-400">…</div>;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12" data-testid="booking-page">
      <Link to={`/fleet/${car.id}`} className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-sm font-mono-ui tracking-widest uppercase" data-testid="back-to-car">
        <ArrowLeft size={16} /> {t.common.backToFleet}
      </Link>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <p className="text-label text-[#FFB400]"><span className="line-accent" />{t.booking.eyebrow}</p>
          <h1 className="mt-3 font-heading text-5xl sm:text-6xl font-bold tracking-tighter text-white" data-testid="booking-title">
            {t.booking.title}
          </h1>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-label block mb-2">{t.booking.fullName} *</label>
              <input className="movix-input" value={form.full_name} onChange={onChange("full_name")} data-testid="input-full-name" />
            </div>
            <div>
              <label className="text-label block mb-2">{t.booking.email} *</label>
              <input type="email" className="movix-input" value={form.email} onChange={onChange("email")} data-testid="input-email" />
            </div>
            <div>
              <label className="text-label block mb-2">{t.booking.phone} *</label>
              <input className="movix-input" value={form.phone} onChange={onChange("phone")} data-testid="input-phone" />
            </div>
            <div>
              <label className="text-label block mb-2">{t.booking.pickupLocation} *</label>
              <input className="movix-input" placeholder={t.booking.pickupLocationPlaceholder} value={form.pickup_location} onChange={onChange("pickup_location")} data-testid="input-pickup-location" />
            </div>

            <div className="md:col-span-2">
              <label
                className="flex items-start gap-3 cursor-pointer select-none p-4 border border-white/10 bg-[#0a0a0a] hover:border-[#FFB400]/50 transition-colors"
                data-testid="airport-pickup-toggle-wrap"
              >
                <input
                  type="checkbox"
                  className="mt-1 accent-[#FFB400] w-4 h-4"
                  checked={form.airport_pickup}
                  onChange={toggleAirportPickup}
                  data-testid="airport-pickup-checkbox"
                />
                <span>
                  <span className="block text-white font-medium">
                    {t.booking.airportPickup}
                  </span>
                  <span className="block text-zinc-500 text-xs mt-1">
                    {t.booking.airportPickupHint}
                  </span>
                </span>
              </label>
            </div>

            {form.airport_pickup && (
              <div className="md:col-span-2" data-testid="flight-number-wrapper">
                <label className="text-label block mb-2">
                  {t.booking.flightNumber} *
                </label>
                <input
                  className="movix-input"
                  placeholder={t.booking.flightNumberPlaceholder}
                  value={form.flight_number}
                  onChange={onChange("flight_number")}
                  data-testid="input-flight-number"
                />
              </div>
            )}

            <div className="relative">
              <label className="text-label block mb-2">{t.booking.pickupDate} *</label>
              <button type="button" onClick={() => { setShowPickupCal((v) => !v); setShowReturnCal(false); }} className="movix-input flex items-center justify-between text-left" data-testid="btn-pickup-date">
                <span>{pickup ? toIsoDate(pickup) : "YYYY-MM-DD"}</span>
                <CalendarIcon size={16} className="text-zinc-500" />
              </button>
              {showPickupCal && (
                <div className="absolute z-30 mt-2 bg-[#0a0a0a] border border-white/10 p-2" data-testid="pickup-calendar">
                  <Calendar
                    mode="single"
                    selected={pickup}
                    onSelect={(d) => { setPickup(d); setShowPickupCal(false); if (ret && d && new Date(ret) <= new Date(d)) setRet(undefined); }}
                    disabled={(d) => d < today || isDateBlocked(d)}
                  />
                </div>
              )}
            </div>

            <div className="relative">
              <label className="text-label block mb-2">{t.booking.returnDate} *</label>
              <button type="button" onClick={() => { setShowReturnCal((v) => !v); setShowPickupCal(false); }} className="movix-input flex items-center justify-between text-left" data-testid="btn-return-date">
                <span>{ret ? toIsoDate(ret) : "YYYY-MM-DD"}</span>
                <CalendarIcon size={16} className="text-zinc-500" />
              </button>
              {showReturnCal && (
                <div className="absolute z-30 mt-2 bg-[#0a0a0a] border border-white/10 p-2" data-testid="return-calendar">
                  <Calendar
                    mode="single"
                    selected={ret}
                    onSelect={(d) => { setRet(d); setShowReturnCal(false); }}
                    disabled={(d) => d <= (pickup || today) || isDateBlocked(d)}
                  />
                </div>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="text-label block mb-2">{t.booking.notes}</label>
              <textarea rows={3} className="movix-input" placeholder={t.booking.notesPlaceholder} value={form.notes} onChange={onChange("notes")} data-testid="input-notes" />
            </div>
          </div>
        </div>

        <aside className="lg:col-span-5">
          <div className="bg-[#0d0d0d] border border-white/10 p-6 sticky top-28" data-testid="booking-summary">
            <div className="flex gap-4">
              <div className="w-32 h-24 overflow-hidden bg-[#070707] flex-shrink-0">
                <img src={car.image} alt={car.name} className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="text-label">{car.brand}</p>
                <h3 className="font-heading text-xl text-white">{car.name}</h3>
                <p className="text-xs text-zinc-500 mt-1 font-mono-ui uppercase tracking-widest">{car.category} · {car.year}</p>
              </div>
            </div>

            <div className="mt-6 border-t border-white/10 pt-5 space-y-3 text-sm">
              <div className="flex justify-between text-zinc-400">
                <span>{formatPrice(car.price_per_day)} × {days || 0} {days === 1 ? t.common.day : t.common.days}</span>
                <span className="text-white">{formatPrice(baseTotal)}</span>
              </div>
              <div className="flex justify-between text-zinc-500 text-xs font-mono-ui uppercase tracking-widest">
                <span>Insurance & roadside</span>
                <span className="text-[#FFB400]">included</span>
              </div>
              {referralCheck?.valid && (
                <div className="flex justify-between text-[#FFB400] text-xs font-mono-ui uppercase tracking-widest" data-testid="referral-discount-line">
                  <span>Referral discount</span>
                  <span>-{formatPrice(discount)}</span>
                </div>
              )}
            </div>

            {/* Referral input */}
            <div className="mt-5 border-t border-white/10 pt-5" data-testid="referral-section">
              {referralCheck?.valid ? (
                <div className="flex items-center justify-between bg-[#FFB400]/10 border border-[#FFB400]/30 p-3" data-testid="referral-applied">
                  <div className="flex items-center gap-2 text-[#FFB400]">
                    <Check size={16} />
                    <span className="font-mono-ui text-sm">{referralInput.toUpperCase()}</span>
                    <span className="text-xs text-zinc-400">
                      −{formatPrice(discount)} {lang === "pt" ? "aplicado" : "applied"}
                    </span>
                  </div>
                  <button onClick={clearReferral} className="text-zinc-500 hover:text-white" data-testid="referral-clear">
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div>
                  <label className="text-label block mb-2 flex items-center gap-2">
                    <Tag size={11} /> {lang === "pt" ? "Código de indicação" : "Referral code"}
                  </label>
                  <div className="flex gap-2">
                    <input
                      className="movix-input text-sm"
                      placeholder={lang === "pt" ? "Ex.: MARINA1234" : "e.g. MARINA1234"}
                      value={referralInput}
                      onChange={(e) => setReferralInput(e.target.value.toUpperCase())}
                      data-testid="referral-input"
                    />
                    <button
                      type="button"
                      onClick={() => onCheckReferral()}
                      disabled={referralChecking || !referralInput.trim()}
                      className="btn-outline py-3 px-4 text-sm disabled:opacity-60"
                      data-testid="referral-apply"
                    >
                      {referralChecking ? "…" : lang === "pt" ? "Aplicar" : "Apply"}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-5 border-t border-white/10 pt-5 flex items-baseline justify-between">
              <span className="text-label">{t.common.total}</span>
              <span className="font-heading text-4xl font-bold text-white" data-testid="booking-total">{formatPrice(total)}</span>
            </div>

            <div className="mt-8 space-y-3">
              <button onClick={onPayNow} disabled={submitting} className="btn-primary w-full justify-center glow-coral disabled:opacity-60" data-testid="btn-pay-now">
                <CreditCard size={18} /> {submitting ? t.common.processing : t.booking.payOnline}
              </button>
              <p className="text-xs text-zinc-500 leading-relaxed">{t.booking.payDescription}</p>

              <div className="relative my-2">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
                <div className="relative flex justify-center"><span className="bg-[#0d0d0d] px-3 text-xs text-zinc-500 font-mono-ui uppercase">or</span></div>
              </div>

              <button onClick={onRequestOnly} disabled={submitting} className="btn-outline w-full justify-center disabled:opacity-60" data-testid="btn-request-only">
                <MailCheck size={18} /> {submitting ? t.common.sending : t.booking.requestOnly}
              </button>
              <p className="text-xs text-zinc-500 leading-relaxed">{t.booking.requestDescription}</p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Booking;
